import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Building2,
  MapPin,
  Users,
  Loader2,
  ChevronRight,
  Factory,
  AlertTriangle,
  CheckCircle,
  DollarSign,
} from "lucide-react";

/* ─── color helpers ─── */
const scoreColor = (s) => {
  if (s >= 80) return "text-emerald-600";
  if (s >= 60) return "text-blue-600";
  if (s >= 40) return "text-amber-600";
  return "text-red-600";
};
const scoreBg = (s) => {
  if (s >= 80) return "bg-emerald-500";
  if (s >= 60) return "bg-blue-500";
  if (s >= 40) return "bg-amber-500";
  return "bg-red-500";
};
const tierGradient = {
  "Tier 1": "from-emerald-50 to-emerald-100/50 border-emerald-200",
  "Tier 2": "from-amber-50 to-amber-100/50 border-amber-200",
  "Tier 3": "from-red-50 to-red-100/50 border-red-200",
};
const tierBadgeColor = {
  "Tier 1": "bg-emerald-100 text-emerald-800",
  "Tier 2": "bg-amber-100 text-amber-800",
  "Tier 3": "bg-red-100 text-red-800",
};
const fmtCurrency = (v) => {
  if (v == null || v === 0) return "N/A";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
};

/* ─── Small supplier node card ─── */
const SupplierNode = ({ node, compact = false }) => (
  <Link
    to={`/admin/supplier/${node.id}`}
    className={`block rounded-xl border bg-gradient-to-br ${tierGradient[node.tier] || "from-gray-50 to-gray-100/50 border-gray-200"} p-3 hover:shadow-md transition-all hover:-translate-y-0.5 cursor-pointer`}
    data-testid={`sc-node-${node.id}`}
  >
    <div className="flex items-start justify-between gap-2 mb-1.5">
      <p className="font-medium text-sm leading-tight line-clamp-2">{node.name}</p>
      <div className={`w-10 h-10 rounded-lg ${scoreBg(node.overall_score)} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
        {node.overall_score?.toFixed(0)}
      </div>
    </div>
    {!compact && (
      <div className="space-y-1 text-xs text-muted-foreground">
        {node.city && node.country && (
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{node.city}, {node.country}</span>
        )}
        {node.capability && (
          <span className="flex items-center gap-1 line-clamp-1"><Factory className="w-3 h-3 flex-shrink-0" />{node.capability}</span>
        )}
      </div>
    )}
    <div className="mt-2 flex items-center gap-1.5">
      <Badge className={`text-[10px] px-1.5 py-0 ${tierBadgeColor[node.tier] || "bg-gray-100"}`}>{node.tier}</Badge>
      <Badge variant="outline" className="text-[10px] px-1.5 py-0">{node.maturity_tier}</Badge>
    </div>
  </Link>
);

/* ─── Arrow connector between columns ─── */
const FlowArrow = ({ label }) => (
  <div className="flex flex-col items-center justify-center px-2 py-4" data-testid="flow-arrow">
    <div className="h-px w-8 bg-border" />
    <div className="flex items-center gap-1 my-1">
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </div>
    {label && <span className="text-[10px] text-muted-foreground uppercase tracking-wider whitespace-nowrap">{label}</span>}
  </div>
);

/* ─── Tier column ─── */
const TierColumn = ({ tierName, suppliers, summary, maxShown = 8 }) => {
  const [showAll, setShowAll] = useState(false);
  const sorted = [...(suppliers || [])].sort((a, b) => b.overall_score - a.overall_score);
  const visible = showAll ? sorted : sorted.slice(0, maxShown);
  const riskIcon = summary?.at_risk > 0 ? <AlertTriangle className="w-3.5 h-3.5 text-red-500" /> : <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />;

  return (
    <div className="flex-1 min-w-[240px]" data-testid={`tier-column-${tierName.replace(/\s/g, "-").toLowerCase()}`}>
      {/* Column header */}
      <div className={`rounded-xl border bg-gradient-to-br ${tierGradient[tierName] || ""} p-4 mb-3`}>
        <div className="flex items-center justify-between mb-2">
          <Badge className={`${tierBadgeColor[tierName]} text-sm px-3`}>{tierName}</Badge>
          <span className="text-xs text-muted-foreground">{summary?.count || 0} suppliers</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Avg Score</p>
            <p className={`text-lg font-bold ${scoreColor(summary?.avg_score || 0)}`}>{summary?.avg_score || 0}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Spend</p>
            <p className="text-sm font-bold">{fmtCurrency(summary?.total_spend)}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">At Risk</p>
            <p className="text-sm font-bold flex items-center justify-center gap-1">{riskIcon}{summary?.at_risk || 0}</p>
          </div>
        </div>
      </div>
      {/* Supplier cards */}
      <div className="space-y-2">
        {visible.map((node) => (
          <SupplierNode key={node.id} node={node} />
        ))}
        {sorted.length > maxShown && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            className="w-full text-center text-xs text-forest font-medium py-2 hover:underline"
            data-testid={`show-all-${tierName.replace(/\s/g, "-").toLowerCase()}`}
          >
            Show {sorted.length - maxShown} more
          </button>
        )}
      </div>
    </div>
  );
};

/* ─── OEM Column (destination) ─── */
const OEMColumn = () => (
  <div className="flex-shrink-0 w-[180px]" data-testid="oem-column">
    <div className="rounded-xl border bg-gradient-to-br from-forest/5 to-forest/10 border-forest/20 p-4 h-full flex flex-col items-center justify-center text-center min-h-[200px]">
      <Building2 className="w-10 h-10 text-forest mb-3" />
      <p className="font-heading font-bold text-lg text-forest">OEM</p>
      <p className="text-xs text-muted-foreground mt-1">Final Product Assembly & Distribution</p>
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   FULL SUPPLY CHAIN MAP (for Admin Dashboard)
   ═══════════════════════════════════════════ */
export const FullSupplyChainMap = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-forest animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { tiers, tier_summary } = data;

  return (
    <div className="space-y-6" data-testid="full-supply-chain-map">
      {/* Legend / description */}
      <Card className="border-border/50 bg-white">
        <CardContent className="py-4">
          <div className="flex items-center gap-6 flex-wrap text-sm">
            <span className="font-medium text-foreground">Material Flow:</span>
            <span className="flex items-center gap-2">
              <Badge className="bg-red-100 text-red-800">Tier 3</Badge>
              <span className="text-muted-foreground">Raw Materials</span>
            </span>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
            <span className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-800">Tier 2</Badge>
              <span className="text-muted-foreground">Components</span>
            </span>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
            <span className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-800">Tier 1</Badge>
              <span className="text-muted-foreground">Assembly</span>
            </span>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
            <span className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-forest" />
              <span className="text-muted-foreground">OEM</span>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Map grid */}
      <div className="flex gap-1 items-start overflow-x-auto pb-4">
        <TierColumn tierName="Tier 3" suppliers={tiers["Tier 3"]} summary={tier_summary["Tier 3"]} maxShown={6} />
        <FlowArrow label="raw materials" />
        <TierColumn tierName="Tier 2" suppliers={tiers["Tier 2"]} summary={tier_summary["Tier 2"]} maxShown={6} />
        <FlowArrow label="components" />
        <TierColumn tierName="Tier 1" suppliers={tiers["Tier 1"]} summary={tier_summary["Tier 1"]} maxShown={6} />
        <FlowArrow label="assembly" />
        <OEMColumn />
      </div>

      {/* Risk Exposure Summary */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" /> Tier-Level Risk Exposure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            {["Tier 3", "Tier 2", "Tier 1"].map((tier) => {
              const s = tier_summary[tier] || {};
              const riskPct = s.count > 0 ? ((s.at_risk / s.count) * 100).toFixed(0) : 0;
              return (
                <div key={tier} className={`rounded-xl border bg-gradient-to-br ${tierGradient[tier]} p-4`} data-testid={`risk-${tier.replace(/\s/g, "-").toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-3">
                    <Badge className={tierBadgeColor[tier]}>{tier}</Badge>
                    <span className="text-xs text-muted-foreground">{s.count} suppliers</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Avg ESG Score</span>
                      <span className={`font-bold ${scoreColor(s.avg_score)}`}>{s.avg_score}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">At-Risk Suppliers</span>
                      <span className="font-bold text-red-600">{s.at_risk} ({riskPct}%)</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Spend</span>
                      <span className="font-bold">{fmtCurrency(s.total_spend)}</span>
                    </div>
                    <div className="h-2 bg-white/60 rounded-full overflow-hidden mt-1">
                      <div className={`h-full rounded-full ${scoreBg(s.avg_score)}`} style={{ width: `${Math.min(s.avg_score, 100)}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   SUPPLIER SUPPLY CHAIN (for individual Supplier Detail)
   ═══════════════════════════════════════════════════════ */
export const SupplierSupplyChain = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-forest animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { current, upstream, downstream, tier_flow } = data;

  return (
    <div className="space-y-6" data-testid="supplier-supply-chain">
      {/* Flow indicator */}
      <Card className="border-border/50 bg-white">
        <CardContent className="py-4">
          <div className="flex items-center gap-3 flex-wrap text-sm">
            <span className="font-medium">Position in Supply Chain:</span>
            {tier_flow.map((t, i) => (
              <span key={t} className="flex items-center gap-2">
                {i > 0 && <ArrowRight className="w-4 h-4 text-muted-foreground" />}
                <Badge className={t === current.tier ? "bg-forest text-white" : tierBadgeColor[t] || "bg-gray-100 text-gray-800"}>
                  {t}
                </Badge>
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Current supplier highlight */}
      <Card className="border-2 border-forest bg-forest/5">
        <CardContent className="py-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-forest flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
              {current.overall_score?.toFixed(0)}
            </div>
            <div className="flex-1">
              <p className="font-heading font-bold text-lg">{current.name}</p>
              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                <Badge className={tierBadgeColor[current.tier]}>{current.tier}</Badge>
                {current.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{current.city}, {current.country}</span>}
                {current.capability && <span className="flex items-center gap-1"><Factory className="w-3 h-3" />{current.capability}</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upstream & Downstream grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Upstream */}
        <div>
          <h3 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
            <ArrowRight className="w-4 h-4 rotate-180 text-muted-foreground" />
            Upstream Suppliers
            <span className="text-xs text-muted-foreground font-normal">({upstream.length} connected)</span>
          </h3>
          {upstream.length > 0 ? (
            <div className="space-y-2" data-testid="upstream-suppliers">
              {upstream.map((node) => (
                <SupplierNode key={node.id} node={node} />
              ))}
            </div>
          ) : (
            <Card className="border-border/50 border-dashed">
              <CardContent className="py-8 text-center text-muted-foreground text-sm">
                No upstream suppliers mapped
              </CardContent>
            </Card>
          )}
        </div>

        {/* Downstream */}
        <div>
          <h3 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
            Downstream Customers
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-normal">({downstream.length} connected)</span>
          </h3>
          {downstream.length > 0 ? (
            <div className="space-y-2" data-testid="downstream-suppliers">
              {downstream.map((node) => (
                <SupplierNode key={node.id} node={node} />
              ))}
            </div>
          ) : (
            <Card className="border-border/50 border-dashed">
              <CardContent className="py-8 text-center text-muted-foreground text-sm">
                {current.tier === "Tier 1" ? "Supplies directly to OEM" : "No downstream customers mapped"}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
